export const handleExportExcel = async (customers, companyName, isSelection = false) => {
    try {
        const XLSX = await import('xlsx');
        const exportData = customers.map(c => ({
            'Customer ID': c.customerId || '',
            'Name': c.name || '',
            'Category': c.categoryName || '',
            'Contact Person': c.contactPerson || '',
            'Designation': c.designation || '',
            'Mobile Number': c.mobileNumber || '',
            'WhatsApp Number': c.whatsappNumber || '',
            'Email': c.email || '',
            'TRN': c.trn || '',
            'Address': c.address || '',
            'Country': c.countryName || '',
            'State': c.stateName || '',
            'Assigned Salespersons': c.assignedSalesPersons?.map(s => s.fullName).join(', ') || '',
            'Delegated Salespersons': c.delegatedSalesPersons?.map(s => s.fullName).join(', ') || '',
            'Price Type': c.priceType || 'RETAIL'
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Customers');

        const dateStr = new Date().toISOString().split('T')[0];
        let exportName = companyName;

        if (isSelection) {
            if (customers.length === 1) exportName = customers[0].name.replace(/\s+/g, '_').toLowerCase();
            else exportName = 'selection';
        }

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `customers_export_${exportName}_${dateStr}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
    } catch (err) {
        console.error('Export failed:', err);
        throw err;
    }
};

export const handleDownloadTemplate = async (lookupData) => {
    try {
        const XLSX = await import('xlsx');
        const workbook = XLSX.utils.book_new();

        const headers = [['Customer ID', 'Name', 'Category', 'Contact Person', 'Designation', 'Mobile Number', 'WhatsApp Number', 'Email', 'TRN', 'Address', 'Country', 'State', 'Assigned Salesperson', 'Delegated Salesperson', 'Price Type']];
        const worksheet = XLSX.utils.aoa_to_sheet(headers);
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Import Template');

        const lookupInfo = [
            ['FIELD', 'AVAILABLE OPTIONS (Type exactly as shown)'],
            ['Category', lookupData.categories.map(c => c.name).join(', ')],
            ['Country', lookupData.countries.map(c => c.name).join(', ')],
            ['State (for UAE)', lookupData.states.map(s => s.name).join(', ')],
            ['Salesperson (Assigned / Delegated)', lookupData.salesPersons.map(s => s.name).join(', ')],
            ['Price Type', 'RETAIL, WHOLESALE']
        ];
        const lookupSheet = XLSX.utils.aoa_to_sheet(lookupInfo);
        XLSX.utils.book_append_sheet(workbook, lookupSheet, 'Available_Options');

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'customer_import_template.xlsx';
        a.click();
        URL.revokeObjectURL(url);
    } catch (err) {
        console.error('Template download failed:', err);
        throw err;
    }
};

export const parseImportFile = async (file, lookupData) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const XLSX = await import('xlsx');
                const data = new Uint8Array(evt.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                if (jsonData.length === 0) {
                    throw new Error('The selected file is empty.');
                }

                // Dynamically find the header row (scan up to top 15 rows)
                let headerRowIdx = -1;
                for (let i = 0; i < Math.min(jsonData.length, 15); i++) {
                    const row = jsonData[i] || [];
                    const hasNameHeader = row.some(cell => 
                        cell && ['name', 'customer name', 'customer_name', 'customerid', 'customer id', 'customer_id'].some(kw => 
                            cell.toString().toLowerCase().replace(/[\s\-_]/g, '').includes(kw.replace(/[\s\-_]/g, ''))
                        )
                    );
                    if (hasNameHeader) {
                        headerRowIdx = i;
                        break;
                    }
                }

                // Fallback to row 0 if no clear headers found
                if (headerRowIdx === -1) {
                    headerRowIdx = 0;
                }

                const headers = jsonData[headerRowIdx] || [];
                const getColIndex = (names) => {
                    return headers.findIndex(h => 
                        h && names.some(name => {
                            const cleanedH = h.toString().toLowerCase().replace(/\s+/g, ' ').trim();
                            const cleanedName = name.toLowerCase().replace(/\s+/g, ' ').trim();
                            return cleanedH.includes(cleanedName);
                        })
                    );
                };

                const customerIdIdx = getColIndex(['customer id']);
                const nameIdx = getColIndex(['name', 'customer name']);
                const categoryIdx = getColIndex(['category']);
                const contactPersonIdx = getColIndex(['contact person', 'contact p']);
                const designationIdx = getColIndex(['designation', 'designati']);
                const mobileIdx = getColIndex(['mobile number', 'mobile n', 'mobile']);
                const whatsappIdx = getColIndex(['whatsapp number', 'whatsap', 'whatsapp']);
                const emailIdx = getColIndex(['email']);
                const trnIdx = getColIndex(['trn']);
                const addressIdx = getColIndex(['address']);
                const countryIdx = getColIndex(['country']);
                const stateIdx = getColIndex(['state']);
                const assignedIdx = getColIndex(['assigned salespersons', 'assigned salesperson', 'assigned sales person', 'assigned', 'salesperson']);
                const delegatedIdx = getColIndex(['delegated salespersons', 'delegated salesperson', 'delegated sales person', 'delegated', 'delegate']);
                const priceTypeIdx = getColIndex(['price type']);

                if (nameIdx === -1) {
                    throw new Error('Required column "Customer Name" or "Name" was not found in the Excel sheet headers. Please verify column headers.');
                }

                const getVal = (row, idx) => {
                    if (idx === -1 || idx === undefined || row[idx] === undefined || row[idx] === null) {
                        return '';
                    }
                    return row[idx].toString().trim();
                };

                const customersToImport = [];
                const rows = jsonData.slice(headerRowIdx + 1);

                const categoryMap = Object.fromEntries(lookupData.categories.map(c => [c.name?.trim().toLowerCase(), c.publicId]));
                const salesPersonMap = Object.fromEntries(
                    lookupData.salesPersons.flatMap(s => {
                        const entries = [];
                        if (s.name) entries.push([s.name.trim().toLowerCase(), s.publicId]);
                        if (s.fullName) entries.push([s.fullName.trim().toLowerCase(), s.publicId]);
                        return entries;
                    })
                );
                const countryMap = Object.fromEntries(lookupData.countries.map(c => [c.name?.trim().toLowerCase(), c.publicId]));
                const stateMap = Object.fromEntries(lookupData.states.map(s => [s.name?.trim().toLowerCase(), s.publicId]));

                const parseSalesPersonIds = (valStr) => {
                    if (!valStr) return [];
                    const names = valStr.split(/[,;]/).map(n => n.trim().toLowerCase()).filter(Boolean);
                    return names.map(n => salesPersonMap[n]).filter(Boolean);
                };

                rows.forEach(r => {
                    const nameVal = getVal(r, nameIdx);
                    if (!nameVal) return; // Skip empty rows

                    const catVal = getVal(r, categoryIdx).toLowerCase();
                    const countryVal = getVal(r, countryIdx).toLowerCase();
                    const stateVal = getVal(r, stateIdx).toLowerCase();
                    
                    const priceTypeRaw = getVal(r, priceTypeIdx).toUpperCase();
                    const priceType = (priceTypeRaw === 'WHOLESALE' || priceTypeRaw === 'RETAIL') ? priceTypeRaw : 'RETAIL';

                    const customer = {
                        customerId: getVal(r, customerIdIdx),
                        customerName: nameVal,
                        categoryPublicId: categoryMap[catVal] || null,
                        contactPerson: getVal(r, contactPersonIdx),
                        designation: getVal(r, designationIdx),
                        mobileNumber: getVal(r, mobileIdx),
                        whatsappNumber: getVal(r, whatsappIdx),
                        email: getVal(r, emailIdx),
                        trn: getVal(r, trnIdx),
                        address: getVal(r, addressIdx),
                        countryPublicId: countryMap[countryVal] || null,
                        statePublicId: stateMap[stateVal] || null,
                        assignedSalesPersonPublicIds: parseSalesPersonIds(getVal(r, assignedIdx)),
                        delegatedSalesPersonPublicIds: parseSalesPersonIds(getVal(r, delegatedIdx)),
                        priceType: priceType
                    };
                    customersToImport.push(customer);
                });

                if (customersToImport.length === 0) {
                    throw new Error('No valid customer data rows found below the header row.');
                }

                resolve(customersToImport);
            } catch (err) {
                reject(err);
            }
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
    });
};
